import { useCallback, useEffect, useRef } from 'react';

import fetcher from 'api/fetcher';
import Debug from 'utils/debug';

import { CampaignModerationInfo, setCampaignModerationList } from 'store/models/moderation';
import { useDispatch, useSelector } from 'store/utils';

const POLLING_INTERVAL = 10 * 60 * 1000;
const MIN_BANNERS_THRESHOLD = 10;

export const useModerationListPolling = (): void => {
    const dispatch = useDispatch();
    const campaignModerationList = useSelector((state) => state.moderation.campaignModerationList);
    const bannersCount = campaignModerationList.reduce((total, campaign) => total + campaign.banners.length, 0);

    const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const abortControllerRef = useRef<AbortController | null>(null);

    const fetchModerationList = useCallback(async () => {
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }

        abortControllerRef.current = new AbortController();

        try {
            const response = await fetcher.get<unknown, CampaignModerationInfo[]>('/api/v1/moderation/list', {
                signal: abortControllerRef.current.signal,
            });
            dispatch(setCampaignModerationList(response));
        } catch (error) {
            if (error instanceof Error && error.name === 'AbortError') {
                return;
            }

            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
            Debug.log('Ошибка при загрузке списка модерации:', error);
        }
    }, [dispatch]);

    const scheduleNextPoll = useCallback(() => {
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
        }

        intervalRef.current = setInterval(() => {
            void fetchModerationList();
        }, POLLING_INTERVAL);
    }, [fetchModerationList]);

    useEffect(() => {
        if (campaignModerationList.length === 0) {
            void fetchModerationList();
        }

        scheduleNextPoll();

        if (bannersCount < MIN_BANNERS_THRESHOLD && campaignModerationList.length > 0) {
            void fetchModerationList();
            scheduleNextPoll();
        }

        return () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }

            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, [fetchModerationList, scheduleNextPoll, campaignModerationList.length, bannersCount]);
};
